import axios from "axios";
import { getHeaders } from "../utility/getHeader";

/**
 * Fetch analytics data from various analytics endpoints
 * @param {string} endpoint - The analytics endpoint (e.g., 'comprehensive-summary', 'payment-methods')
 * @param {object} params - Query parameters (e.g., { period: 'today', limit: 10 })
 * @param {function} setData - Optional state setter function
 * @returns {Promise} - Returns the fetched data
 */
const fetchAnalyticsData = async (endpoint, params = {}, setData = null) => {
    try {
        let url = `/analytics/${endpoint}`;
        
        // Add query parameters if provided
        if (params && Object.keys(params).length > 0) {
            const queryParams = new URLSearchParams(params).toString();
            url += `?${queryParams}`;
        }
        
        const response = await axios.get(url, getHeaders());
        
        // If a setData function is provided, update the state with the fetched data
        if (setData && typeof setData === 'function') {
            setData(response.data);
        }
        
        return response.data;
    } catch (error) {
        console.error(`fetchAnalyticsData error for ${endpoint}:`, error);
        
        // If setData function is provided, set empty data on error
        if (setData && typeof setData === 'function') {
            setData(null);
        }
        
        // Return null on error (you can modify this behavior as needed)
        return null;
    }
};

/**
 * Fetch dashboard data from dashboard endpoints
 * @param {string} endpoint - The dashboard endpoint (e.g., 'analytics-summary', 'today-sales')
 * @param {object} params - Query parameters (e.g., { period: 'today', limit: 10 })
 * @param {function} setData - Optional state setter function
 * @returns {Promise} - Returns the fetched data
 */
const fetchDashboardData = async (endpoint, params = {}, setData = null) => {
    try {
        let url = `/dashboard/${endpoint}`;
        
        // Add query parameters if provided
        if (params && Object.keys(params).length > 0) {
            const queryParams = new URLSearchParams(params).toString();
            url += `?${queryParams}`;
        }
        
        const response = await axios.get(url, getHeaders());
        
        // If a setData function is provided, update the state with the fetched data
        if (setData && typeof setData === 'function') {
            setData(response.data);
        }
        
        return response.data;
    } catch (error) {
        console.error(`fetchDashboardData error for ${endpoint}:`, error);
        
        // If setData function is provided, set empty data on error
        if (setData && typeof setData === 'function') {
            setData(null);
        }
        
        // Return null on error (you can modify this behavior as needed)
        return null;
    }
};

/**
 * Fetch report data from various report endpoints
 * @param {string} endpoint - The report endpoint (e.g., 'weekly-sales', 'monthly-sales')
 * @param {object} params - Query parameters (e.g., { period: 'month', date: '2024-01-01' })
 * @param {function} setData - Optional state setter function
 * @returns {Promise} - Returns the fetched data
 */
const fetchReportData = async (endpoint, params = {}, setData = null) => {
    try {
        let url = `/report/${endpoint}`;
        
        // Add query parameters if provided
        if (params && Object.keys(params).length > 0) {
            const queryParams = new URLSearchParams(params).toString();
            url += `?${queryParams}`;
        }
        
        const response = await axios.get(url, getHeaders());
        
        // If a setData function is provided, update the state with the fetched data
        if (setData && typeof setData === 'function') {
            setData(response.data);
        }
        
        return response.data;
    } catch (error) {
        console.error(`fetchReportData error for ${endpoint}:`, error);
        
        // If setData function is provided, set empty data on error
        if (setData && typeof setData === 'function') {
            setData(null);
        }
        
        // Return null on error (you can modify this behavior as needed)
        return null;
    }
};

export { fetchAnalyticsData, fetchDashboardData, fetchReportData };
export default fetchAnalyticsData;
